/**
 * Azioni sul CRM nei suggerimenti dell'assistente (logica pura, niente React).
 *
 * Le azioni arrivano dalla edge whatsapp-agent-analyze (gia' validate contro il
 * catalogo dell'organizzazione) nella colonna crm_actions. Qui:
 * - lettura difensiva (righe vecchie senza colonna, JSON malformato);
 * - testi della UI in italiano;
 * - controllo che l'azione valga ancora sulla scheda di adesso (fase ancora
 *   presente, stessa pipeline, utente ancora attivo, valore gia' aggiornato);
 * - cambio di stato (confermata / scartata) e chiusura del suggerimento.
 */
import type {
  CrmAction, CrmActionStatus, UpdateLeadAction, UpdatableLeadField,
} from '../../../../supabase/functions/whatsapp-agent-analyze/crmActions';

export type { CrmAction, CrmActionStatus };

const KINDS = new Set(['move_stage', 'mark_lost', 'create_deal', 'update_lead', 'assign']);

/** Azioni lette dalla riga: [] se la colonna manca o il contenuto non e' valido. */
export function normalizeCrmActions(raw: unknown): CrmAction[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((a): a is CrmAction =>
    !!a && typeof a === 'object'
    && typeof (a as CrmAction).id === 'string'
    && KINDS.has((a as CrmAction).kind)
    && ['proposed', 'applied', 'dismissed'].includes((a as CrmAction).status));
}

export const pendingCrmActions = (raw: unknown): CrmAction[] =>
  normalizeCrmActions(raw).filter((a) => a.status === 'proposed');

/** Nuovo array con lo stato dell'azione cambiato (le altre restano com'erano). */
export function setActionStatus(raw: unknown, actionId: string, status: CrmActionStatus): CrmAction[] {
  return normalizeCrmActions(raw).map((a) => (a.id === actionId ? { ...a, status } : a));
}

/**
 * Un suggerimento "solo CRM" si chiude quando non restano azioni da decidere;
 * gli altri tipi restano (c'e' ancora la richiesta del cliente da gestire).
 */
export function shouldCloseSuggestion(requestType: string, actions: CrmAction[]): boolean {
  return requestType === 'crm_update' && actions.length > 0 && actions.every((a) => a.status !== 'proposed');
}

/** Titolo dell'azione come si legge nella card. */
export function crmActionTitle(action: CrmAction): string {
  switch (action.kind) {
    case 'move_stage': return `Sposta in fase "${action.stage_name}"`;
    case 'mark_lost': return action.lost_reason ? `Segna come perso: ${action.lost_reason}` : 'Segna come perso';
    case 'create_deal': return action.services.length > 0 ? 'Crea offerta con i servizi citati' : 'Crea offerta';
    case 'update_lead': return 'Aggiorna i dati della scheda';
    case 'assign': return `Assegna a ${action.user_name}`;
  }
}

/** Testo del bottone di conferma. */
export function crmActionConfirmLabel(action: CrmAction): string {
  switch (action.kind) {
    case 'move_stage': return 'Sposta';
    case 'mark_lost': return 'Scegli il motivo';
    case 'create_deal': return 'Apri offerta';
    case 'update_lead': return 'Aggiorna';
    case 'assign': return 'Assegna';
  }
}

export interface LeadSnapshot {
  pipeline_id?: string | null;
  assigned_to?: string | null;
  [field: string]: unknown;
}

/**
 * Perche' l'azione non si puo' piu' confermare sulla scheda di adesso
 * (null = si puo'). Serve se nel frattempo qualcuno ha cambiato la scheda.
 */
export function crmActionBlock(
  action: CrmAction,
  lead: LeadSnapshot,
  ctx: { stageIds: string[]; currentStageId: string | null; userIds: string[] },
): string | null {
  switch (action.kind) {
    case 'move_stage':
    case 'mark_lost':
      if ((action.pipeline_id || null) !== (lead.pipeline_id || null)) return 'Il Lead/Cliente è stato spostato in un\'altra pipeline';
      // Liste vuote = ancora in caricamento: non si blocca.
      if (ctx.stageIds.length > 0 && !ctx.stageIds.includes(action.stage_id)) return 'Questa fase non esiste più';
      if (ctx.currentStageId === action.stage_id) return 'È già in questa fase';
      return null;
    case 'assign':
      if (ctx.userIds.length > 0 && !ctx.userIds.includes(action.user_id)) return 'Questo utente non è più attivo';
      if (lead.assigned_to === action.user_id) return 'È già assegnato a questa persona';
      return null;
    case 'update_lead':
      return pendingLeadChanges(action, lead).length === 0 ? 'I dati sono già aggiornati' : null;
    case 'create_deal':
      return null;
  }
}

const valueOf = (lead: LeadSnapshot, field: UpdatableLeadField): string | null => {
  const v = lead[field];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
};

/** Modifiche ancora da fare, con il "prima" letto dalla scheda di adesso. */
export function pendingLeadChanges(action: UpdateLeadAction, lead: LeadSnapshot) {
  return action.changes
    .map((c) => ({ ...c, before: valueOf(lead, c.field) }))
    .filter((c) => (c.before || '').toLowerCase() !== c.after.toLowerCase());
}

/** Dati da passare a updateClient per l'azione "Aggiorna dati". */
export function leadUpdatesFromAction(action: UpdateLeadAction, lead: LeadSnapshot): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of pendingLeadChanges(action, lead)) out[c.field] = c.after;
  return out;
}
